"use client";

import { domAnimation, LazyMotion, m } from "framer-motion";
import { BarChart3, CheckCircle2, ShieldCheck } from "lucide-react";
import { Squares } from "@/components/custom/squares_background";
import OurTeam from "./our_team";

export default function AboutClient() {
	return (
		<LazyMotion features={domAnimation}>
			<div className="about-page bg-white min-h-screen">
				{/* Creative Hero Section - extends behind navbar */}
				<section className="relative h-[60vh] md:min-h-[800px] flex items-center justify-center overflow-hidden bg-zinc-950 text-white -mt-20 pt-20">
					<div className="absolute inset-0 z-0">
						<div className="absolute top-0 right-0 w-[600px] h-[600px] bg-red-600/20 rounded-full blur-[150px] animate-pulse" />
						<div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-[150px]" />
						<Squares
							theme="dark"
							direction="diagonal"
							speed={0.2}
							squareSize={50}
							className="opacity-20"
							hoverFillColor="#ef4444"
						/>
					</div>

					<div className="container mx-auto px-4 relative z-10">
						<div className="max-w-5xl mx-auto">
							<m.div
								initial={{ opacity: 0, y: 50 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.8, ease: "easeOut" }}
							>
								<h1 className="font-syne font-bold uppercase leading-[0.85] tracking-tighter text-center">
									<span className="block text-[12vw] md:text-[8rem] text-gray-50/3 stroke-text text-stroke-2 text-stroke-white/30 hover:text-white/10 transition-colors duration-500">
										We Are
									</span>
									<span className="block text-[12vw] md:text-[8rem] text-red-600 relative">
										TheHighraysYT
									</span>
								</h1>
							</m.div>

							<m.p
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								transition={{ delay: 0.5, duration: 0.8 }}
								className="mt-12 text-center text-zinc-400 text-lg md:text-2xl font-inter max-w-2xl mx-auto leading-relaxed"
							>
								The fuel for your ascent. We turn{" "}
								<span className="text-white font-semibold">ambition</span> into{" "}
								<span className="text-red-500 font-semibold">momentum</span>.
							</m.p>
						</div>
					</div>

					{/* Scroll Indicator */}
					<m.div
						className="absolute bottom-10 left-1/2 -translate-x-1/2"
						initial={{ opacity: 0, y: -20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ delay: 1.2, duration: 0.5 }}
					>
						<div className="w-px h-24 bg-linear-to-b from-transparent via-zinc-500 to-transparent relative overflow-hidden">
							<m.div
								className="absolute top-0 w-full h-1/2 bg-red-500"
								animate={{ y: ["-200%", "200%"] }}
								transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
							/>
						</div>
					</m.div>
				</section>

				{/* Our Story Section */}
				<section className="py-32 bg-white relative">
					<div className="container mx-auto px-4">
						<div className="flex flex-col md:flex-row gap-20 items-stretch">
							<div className="md:w-1/2 sticky top-32 self-start">
								<m.div
									initial={{ opacity: 0, x: -50 }}
									whileInView={{ opacity: 1, x: 0 }}
									viewport={{ once: true, margin: "-100px" }}
									transition={{ duration: 0.8 }}
								>
									<h2 className="font-syne text-5xl md:text-7xl font-bold uppercase tracking-tighter text-zinc-900 leading-[0.9]">
										From <br />
										<span className="text-zinc-300">Zero</span> to <br />
										<span className="text-red-600">Hero</span>
									</h2>
									<div className="w-24 h-1 bg-red-600 mt-8 mb-8" />
									<p className="text-zinc-500 font-inter text-lg">
										The story behind the service that's changing the game.
									</p>
								</m.div>
							</div>

							<div className="md:w-1/2 space-y-16">
								<m.div
									initial={{ opacity: 0, y: 30 }}
									whileInView={{ opacity: 1, y: 0 }}
									viewport={{ once: true, margin: "-100px" }}
									transition={{ duration: 0.6 }}
								>
									<p className="font-inter text-xl md:text-2xl text-zinc-800 leading-relaxed font-light">
										We started TheHighraysYT because we saw too many talented
										creators struggling to get noticed.{" "}
										<strong className="font-semibold text-zinc-900">
											Getting discovered is the hardest part
										</strong>
										. Great content isn't enough — you need the right people to
										see it. That's where we come in.
									</p>
								</m.div>

								<div className="grid gap-8">
									<m.div
										initial={{ opacity: 0, y: 30 }}
										whileInView={{ opacity: 1, y: 0 }}
										viewport={{ once: true }}
										transition={{ delay: 0.2, duration: 0.6 }}
										className="bg-zinc-50 rounded-3xl p-10 border border-zinc-100 hover:border-red-100 transition-colors group"
									>
										<h3 className="font-syne text-3xl font-bold uppercase mb-4 text-zinc-900 group-hover:text-red-600 transition-colors">
											Our Mission
										</h3>
										<p className="font-inter text-zinc-600 text-lg leading-relaxed">
											Help{" "}
											<span className="font-bold text-zinc-900">
												100,000 creators
											</span>{" "}
											find and keep the audience their content deserves.
										</p>
									</m.div>

									<m.div
										initial={{ opacity: 0, y: 30 }}
										whileInView={{ opacity: 1, y: 0 }}
										viewport={{ once: true }}
										transition={{ delay: 0.3, duration: 0.6 }}
										className="bg-zinc-900 rounded-3xl p-10 text-white relative overflow-hidden group"
									>
										<div className="absolute top-0 right-0 w-64 h-64 bg-red-600/10 rounded-full blur-3xl group-hover:bg-red-600/20 transition-colors duration-500" />
										<h3 className="font-syne text-3xl font-bold uppercase mb-4 relative z-10">
											Our Vision
										</h3>
										<p className="font-inter text-zinc-400 text-lg leading-relaxed relative z-10">
											Make creator success accessible to everyone, regardless of
											their starting point, budget, or location.
										</p>
									</m.div>
								</div>
							</div>
						</div>
					</div>
				</section>

				{/* Why Choose Us Section */}
				<section className="py-32 bg-zinc-50 border-y border-zinc-200">
					<div className="container mx-auto px-4">
						<m.div
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							className="text-center max-w-3xl mx-auto mb-20"
						>
							<h2 className="font-syne text-4xl md:text-6xl font-bold uppercase tracking-tighter text-zinc-900 mb-6">
								Why <span className="text-red-600">Choose Us</span>
							</h2>
							<p className="font-inter text-lg text-zinc-600">
								We've helped 2,500+ creators grow. Here's what makes us
								different.
							</p>
						</m.div>

						<div className="grid grid-cols-1 md:grid-cols-3 gap-8">
							<FeatureCard
								icon={BarChart3}
								title="Real Traffic"
								description="All campaign traffic comes through Google Ads — real people discovering your content through paid advertising."
								delay={0}
							/>
							<FeatureCard
								icon={ShieldCheck}
								title="Safe & Compliant"
								description="We run every campaign through Google Ads, fully compliant with platform policies. Your channel stays protected."
								delay={0.1}
							/>
							<FeatureCard
								icon={CheckCircle2}
								title="Transparent"
								description="See exactly where your views come from with detailed analytics and reporting."
								delay={0.2}
							/>
						</div>
					</div>
				</section>

				{/* Our Team Section */}
				<OurTeam />
			</div>
		</LazyMotion>
	);
}

function FeatureCard({
	icon: Icon,
	title,
	description,
	delay,
}: {
	icon: React.ElementType;
	title: string;
	description: string;
	delay: number;
}) {
	return (
		<m.div
			initial={{ opacity: 0, y: 20 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true }}
			transition={{ delay: delay, duration: 0.5 }}
			className="bg-white rounded-3xl p-8 shadow-[0_5px_30px_-15px_rgba(0,0,0,0.1)] border border-zinc-100 hover:shadow-[0_20px_40px_-15px_rgba(220,38,38,0.15)] hover:border-red-100 transition-[box-shadow,border-color] duration-500 group"
		>
			<div className="w-14 h-14 rounded-2xl bg-zinc-50 flex items-center justify-center text-zinc-400 group-hover:bg-red-500 group-hover:text-white transition-[color,background-color,box-shadow] duration-500 mb-8 shadow-sm group-hover:shadow-red-200">
				<Icon className="w-7 h-7" />
			</div>
			<h3 className="font-syne text-xl font-bold uppercase tracking-wide mb-4 text-zinc-900 group-hover:text-red-600 transition-colors">
				{title}
			</h3>
			<p className="font-inter text-zinc-500 leading-relaxed">{description}</p>
		</m.div>
	);
}
